/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useEffect, useState } from 'react';
import { Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Grid,
  FormControl,
  TextField,
  Button,
  Alert,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  Switch,
  // Typography,
} from '@mui/material';
// import { MuiTelInput } from 'mui-tel-input';
// import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import { DEMO_ROLE, PREFERRED_ROLE } from 'modules/common/constants/roles';
import { selectNotification } from 'modules/common/notifications/selectors';
import { selectCommandFilterDropdownList } from 'modules/dashboard/selectors';
import ERROR_TYPES from 'modules/common/constants/error-types';
import { updateUserValidation } from 'modules/users/validation/add-user-form-validation';
import { dashboardActions } from 'modules/dashboard/slice';
import { usersActions } from 'modules/users/slice';
import { selectCurrentUser, selectIsOtpSent, selectLandingPage } from 'modules/users/selectors';
import { notificationActions } from 'modules/common/notifications/slice';
import OutlinedDiv from './outer-card';

/**
 * Update User implementation to update user preferences
 * @param {string} organizationId - organization id
 * @param {function} onClose - function to execute on close
 * @returns
 */
const UpdateUser = ({ organizationId, onClose }) => {
  const dispatch = useDispatch();
  //
  const notification = useSelector(selectNotification);
  const currentUser = useSelector(selectCurrentUser);
  const filterList = useSelector(selectCommandFilterDropdownList);
  const customLandingPage = useSelector(selectLandingPage);
  const isOtpSent = useSelector(selectIsOtpSent);
  //
  const [filterDropdownList, setFilterDropdownList] = useState([]);
  const [initialValues, setInitialValues] = useState({
    email: '',
    username: '',
    firstName: '',
    lastName: '',
   // phoneNumber: '',
    isCustomLandingPageEnabled: false,
    landingPage: '',
    ...(currentUser?.roles?.[0]?.name !== DEMO_ROLE && { demoMode: false }),
    otp: '',
  });
  // Triggers when currentUser changes
  useEffect(() => {
    // set initial values based on current user
    if (currentUser) {
      setInitialValues({
        email: currentUser?.email,
        username: currentUser?.username,
        firstName: currentUser?.firstName,
        lastName: currentUser?.lastName,
       // phoneNumber: currentUser?.phoneNumber,
        isCustomLandingPageEnabled: Boolean(customLandingPage?.landingPage),
        landingPage: customLandingPage?.landingPage ?? '',
        ...(currentUser?.roles?.[0]?.name !== DEMO_ROLE && {
          demoMode: customLandingPage?.isDemoModeActivated ?? false,
        }),
      });
    }
  }, [currentUser, customLandingPage]);
  // transform filter array
  useEffect(() => {
    if (filterList?.results?.length > 0) {
      const transformedList = filterList?.results?.map((type) => ({
        id: type?.commandFilterQuery?.filterName,
        label: type?.commandFilterQuery?.filterName,
        value: type?.commandFilterQuery?.query,
      }));
      setFilterDropdownList(transformedList);
    }
  }, [filterList]);
  // update user details in preference
  const onSubmitUpdateUser = async (values) => {
    const valueObj = {
      email: values?.email,
      firstName: values?.firstName,
      lastName: values?.lastName,
    //  phoneNumber: values?.phoneNumber?.replace(/\s+/g, ''),
    };
    const updateLandingObj = {
      landingPage: values?.isCustomLandingPageEnabled ? values?.landingPage : '',
      ...(currentUser?.roles?.[0]?.name !== DEMO_ROLE && { isDemoModeActivated: values?.demoMode }),
      isAlertEmailBundled: customLandingPage?.isAlertEmailBundled,
      isAuditEmailBundled: customLandingPage?.isAuditEmailBundled,
    };
    //
    if (!values.isCustomLandingPageEnabled) {
      dispatch(dashboardActions.resetLandingPage());
    }
    //
    dispatch(
      usersActions.updatePreference({
        ...valueObj,
        organizationId,
        userId: currentUser?.id,
      })
    );
    //
    dispatch(
      usersActions.updateLandingPage({
        ...updateLandingObj,
        organizationId,
        userId: currentUser?.id,
      })
    );
    dispatch(notificationActions.resetNotification());
  };
  // otp verification
  // const onSubmitChangeOtp = async (values) => {
  //   const payload = {
  //     organizationId,
  //     userId: currentUser?.id,
  //     otp: values.otp,
  //     phoneNumber: values?.phoneNumber?.replace(/\s+/g, ''),
  //   };

  //   dispatch(usersActions.verifyOtp(payload));
  // };
  // reset notifications on first render
  useEffect(() => {
    dispatch(notificationActions.resetNotification());
  }, []);
  //
  return (
    <Box
      xs={12}
      mt={2}
      component={Grid}
      container
      item
      display="flex"
      direction="column"
      sx={{ backgroundColor: 'white', flexShrink: 0 }}
    >
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={updateUserValidation(isOtpSent)}
        onSubmit={onSubmitUpdateUser}
      >
        {({
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
          isSubmitting,
          touched,
          values,
          // validateForm,
        }) => (
          <form noValidate onSubmit={handleSubmit}>
            <Grid alignItems="center" px={4} my={4} xs={12} item>
              {notification?.isEnabled && notification?.type === ERROR_TYPES.ERROR && (
                <Alert mt={2} mb={3} severity={notification?.type}>
                  {notification?.message}
                </Alert>
              )}
            </Grid>
            <OutlinedDiv label="User Information">
              <Grid justifyContent="space-around" alignItems="center" px={4} my={4} xs={6} item>
                <FormControl fullWidth>
                  <TextField
                    name="email"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.email ?? ''}
                    error={Boolean(touched.email && errors.email)}
                    helperText={touched.email && errors.email}
                    label="Email"
                    disabled={
                      (currentUser && currentUser?.roles?.[0]?.name === PREFERRED_ROLE) ?? false
                    }
                    variant="outlined"
                    size="small"
                    onKeyDown={(e) => {
                      e.stopPropagation();
                    }}
                  />
                </FormControl>
              </Grid>
              <Grid justifyContent="space-around" alignItems="center" px={4} my={4} xs={6} item>
                <FormControl fullWidth>
                  <TextField
                    name="username"
                    value={values.username ?? ''}
                    label="Username"
                    variant="outlined"
                    size="small"
                    disabled
                  />
                </FormControl>
              </Grid>
              <Grid justifyContent="space-around" alignItems="center" px={4} my={4} xs={6} item>
                <FormControl fullWidth>
                  <TextField
                    name="firstName"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.firstName ?? ''}
                    error={Boolean(touched.firstName && errors.firstName)}
                    helperText={touched.firstName && errors.firstName}
                    label="First name"
                    variant="outlined"
                    disabled={
                      (currentUser && currentUser?.roles?.[0]?.name === PREFERRED_ROLE) ?? false
                    }
                    size="small"
                    onKeyDown={(e) => {
                      e.stopPropagation();
                    }}
                  />
                </FormControl>
              </Grid>
              <Grid justifyContent="space-around" alignItems="center" px={4} my={4} xs={6} item>
                <FormControl fullWidth>
                  <TextField
                    name="lastName"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.lastName ?? ''}
                    error={Boolean(touched.lastName && errors.lastName)}
                    helperText={touched.lastName && errors.lastName}
                    label="Last name"
                    variant="outlined"
                    disabled={
                      (currentUser && currentUser?.roles?.[0]?.name === PREFERRED_ROLE) ?? false
                    }
                    size="small"
                    onKeyDown={(e) => {
                      e.stopPropagation();
                    }}
                  />
                </FormControl>
              </Grid>
              {/* <Grid justifyContent="space-around" alignItems="center" px={4} my={4} xs={6} item>
                <FormControl fullWidth>
                  <MuiTelInput
                    label="Phone Number"
                    name="phoneNumber"
                    onChange={(newValue) => setFieldValue('phoneNumber', newValue)}
                    variant="outlined"
                    size="small"
                    onKeyDown={(e) => {
                      e.stopPropagation();
                    }}
                    value={values.phoneNumber ?? ''}
                    defaultCountry="US"
                    InputProps={{
                      endAdornment: currentUser?.isPhoneNumberVerified && (
                        <VerifiedRoundedIcon sx={{ color: '#4caf50' }} />
                      ),
                    }}
                  />
                </FormControl>
              </Grid>
              {isOtpSent && (
                <Grid justifyContent="space-around" alignItems="center" px={4} my={4} xs={6} item>
                  <FormControl fullWidth>
                    <TextField
                      name="otp"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.otp ?? ''}
                      InputLabelProps={{ shrink: true }}
                      error={Boolean(touched.otp && errors.otp)}
                      helperText={touched.otp && errors.otp}
                      label="OTP"
                      variant="outlined"
                      size="small"
                      onKeyDown={(e) => {
                        e.stopPropagation();
                      }}
                    />
                  </FormControl>
                  <Grid justifyContent="space-between" alignItems="center" px={4} my={1} item>
                    <Typography sx={{ fontStyle: 'italic' }} textAlign="center">
                      App-less queries, alerts and audits require verification.
                    </Typography>
                  </Grid>
                  <Grid container direction="row" justifyContent="flex-end">
                    <Button
                      variant="contained"
                      color="success"
                      disabled={!isOtpSent}
                      onClick={() => {
                        validateForm().then((errs) => {
                          if (!errs.otp) {
                            onSubmitChangeOtp(values);
                          }
                        });
                      }}
                    >
                      Verify
                    </Button>
                  </Grid>
                </Grid>
              )} */}
            </OutlinedDiv>
            <OutlinedDiv label="Landing Page">
              <Grid justifyContent="space-around" alignItems="center" px={4} my={4} xs={12} item>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="isCustomLandingPageEnabled"
                      checked={values.isCustomLandingPageEnabled}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  }
                  label="Select Custom Landing Page"
                />
              </Grid>
              <Grid justifyContent="space-around" alignItems="center" px={4} my={4} xs={6} item>
                <FormControl fullWidth>
                  <Autocomplete
                    options={filterDropdownList ?? []}
                    disabled={!values.isCustomLandingPageEnabled}
                    getOptionLabel={(option) => (option.label ? option.label : '')}
                    isOptionEqualToValue={(option, value) =>
                      option && value ? option.id === value.id : ''
                    }
                    filterSelectedOptions
                    onChange={(e, value) => {
                      setFieldValue('landingPage', value?.value);
                      if (values.isCustomLandingPageEnabled) {
                        dispatch(dashboardActions.setLandingPage(value));
                      }
                    }}
                    onKeyDown={(e) => {
                      e.stopPropagation();
                    }}
                    onBlur={handleBlur}
                    disableClearable
                    value={
                      filterDropdownList?.find((option) => option.value === values.landingPage) ||
                      ''
                    }
                    name="landingPage"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Landing Page"
                        size="medium"
                        error={Boolean(touched.landingPage && errors.landingPage)}
                        helperText={touched.landingPage && errors.landingPage}
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                </FormControl>
              </Grid>
            </OutlinedDiv>
            {currentUser && currentUser?.roles?.[0]?.name !== DEMO_ROLE && (
              <OutlinedDiv label="Demo Mode">
                <Grid justifyContent="space-around" alignItems="center" px={4} my={4} xs={12} item>
                  <FormControlLabel
                    control={
                      <Switch
                        name="demoMode"
                        checked={values.demoMode}
                        onChange={handleChange}
                        size="medium"
                      />
                    }
                    label="Anonymize Hotel Names"
                    labelPlacement="end"
                    sx={{
                      width: 'fit-content',
                      height: 5,
                    }}
                  />
                </Grid>
              </OutlinedDiv>
            )}
            <Grid container direction="row" justifyContent="flex-end">
              <Button
                variant="contained"
                color="primary"
                onClick={() => onClose()}
                sx={{ marginLeft: 2, marginRight: 4 }}
              >
                Cancel
              </Button>
              <Button type="submit" variant="contained" color="success" disabled={isSubmitting}>
                Save
              </Button>
            </Grid>
          </form>
        )}
      </Formik>
    </Box>
  );
};
//
export default UpdateUser;
